/**
 * Lane planner: sidebar module page (route /lane-planner) with Search and
 * Saved tabs. The left pane holds the search form; the right pane shows the
 * quotes every connected vendor returns, through the vendor-neutral
 * `/v1/lane-planner` API. Flight, Ocean and Road can be ticked together, for
 * the modes the company's vendors quote; Multi modal is visible but disabled.
 * A bookable quote opens the full-page booking flow when the booking gate is on.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Dropdown, message, Modal } from 'antd';
import dayjs from 'dayjs';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useToken } from '../../../common/helper';
import { useLaneBookingGate } from '@/hooks/useLaneBookingGate';
import { CustomInput } from '../../../components';
import { GoogleMapsScript } from '../../../components/GoogleMapsProvider';
import {
  GetLaneConfig,
  GetSavedLaneSearches,
  LaneConfig,
  LaneMode,
  LanePage,
  LaneSearchRequest,
  laneErrorMessage,
  NormalizedQuote,
  SavedLaneSearch,
  SaveLaneQuote,
  SaveLaneSearch,
  unwrapLanePayload,
} from '../../../apis/lanePlanner';
import BookingPage from './BookingPage';
import LaneSearchForm from './LaneSearchForm';
import ModeTiles from './ModeTiles';
import { modesAfterConfig, SearchMeta, withoutTrucking } from './laneForm';
import ResultsPane from './ResultsPane';
import SavedPane from './SavedPane';
import { rerunnableRequest } from './savedFormat';
import { placeValueFromRequest } from './placeValue';
import type { SearchCandidates } from './siteSelection';
import { useLaneSearch } from './useLaneSearch';
import { MODE_TITLES, vendorNameOf } from './quoteView';
import { quoteShareText } from './quoteShare';
import type { TruckingChoices } from './truckingView';
import styles from './styles.module.css';

/** Cap for the Search tab's quick-load dropdown; the full list lives on Saved. */
const SAVED_SEARCH_DROPDOWN_LIMIT = 10;

type PageTab = 'search' | 'saved';

/** Modes offered until the company's config says otherwise (what existed before Road). */
const DEFAULT_AVAILABLE: ReadonlySet<LaneMode> = new Set(['air', 'ocean']);

const NO_CHOICES: TruckingChoices = {};

const LanePlannerPage: React.FC = () => {
  // the API functions ignore the argument (the interceptor reads the session token itself)
  const { token: sessionToken } = useToken();
  const token = sessionToken ?? '';
  const navigate = useNavigate();
  const bookingOn = useLaneBookingGate();
  // a booking is being sent or confirmed: leaving would drop its answer
  const [bookingBusy, setBookingBusy] = useState(false);
  const [bookingQuote, setBookingQuote] = useState<NormalizedQuote | null>(
    null
  );
  const {
    search,
    searching,
    error,
    start,
    load,
    retryVendors,
    addLanes,
    reset,
  } = useLaneSearch(token);
  // `?search=<id>` opens a search that already ran (the chat's "See more").
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedSearchId = searchParams.get('search');
  const openedSearchId = useRef<string | null>(null);

  const [tab, setTab] = useState<PageTab>('search');
  const [modes, setModes] = useState<LaneMode[]>(['air']);
  const [available, setAvailable] =
    useState<ReadonlySet<LaneMode>>(DEFAULT_AVAILABLE);
  /** The company has no vendor enabled, so there is nothing to search. */
  const [noVendors, setNoVendors] = useState(false);
  /** The company's vendor setup could not be loaded, so the modes shown are guesses. */
  const [configFailed, setConfigFailed] = useState(false);
  /** The company's vendors can add pickup and delivery trucking to air and ocean searches. */
  const [doorToDoor, setDoorToDoor] = useState(false);
  const [packagingOption, setPackagingOption] = useState<string | undefined>(
    undefined
  );
  /** Optional quote reference (PO / internal id) from the Ocean form. */
  const [reference, setReference] = useState<string | undefined>(undefined);
  const [candidates, setCandidates] = useState<SearchCandidates | undefined>(
    undefined
  );

  // Carrier picks outlive the results pane (the Saved tab unmounts it) but not their search.
  const [picks, setPicks] = useState<{
    searchId: string;
    choices: TruckingChoices;
  } | null>(null);
  const truckingChoices =
    picks && picks.searchId === search?.id ? picks.choices : NO_CHOICES;
  const chooseTrucking = (legKey: string, carrierId: string): void => {
    if (!search) return;
    setPicks((prev) => ({
      searchId: search.id,
      choices: {
        ...(prev?.searchId === search.id ? prev.choices : NO_CHOICES),
        [legKey]: carrierId,
      },
    }));
  };

  const [savedQuoteIds, setSavedQuoteIds] = useState<ReadonlySet<string>>(
    new Set()
  );
  const [savingQuoteIds, setSavingQuoteIds] = useState<ReadonlySet<string>>(
    new Set()
  );

  // A saved entry loaded via Run / Load rates pre-populates the form. The key
  // bump remounts the form so antd initialValues take effect.
  const [prefill, setPrefill] = useState<LaneSearchRequest | null>(null);
  const [prefillKey, setPrefillKey] = useState(0);

  const [saveSearchOpen, setSaveSearchOpen] = useState(false);
  const [saveSearchName, setSaveSearchName] = useState('');
  const [savingSearch, setSavingSearch] = useState(false);
  const [savedRefreshKey, setSavedRefreshKey] = useState(0);
  const [savedSearches, setSavedSearches] = useState<SavedLaneSearch[]>([]);

  // Saved searches for the left pane's "See saved searches" dropdown.
  // Expired ones (ship date in the past) are left out: vendors reject past
  // ship dates, so running them would only error.
  useEffect(() => {
    let cancelled = false;
    setSavedSearches([]);
    GetSavedLaneSearches(token, 100)
      .then((res) => {
        if (cancelled) return;
        const rows = unwrapLanePayload<LanePage<SavedLaneSearch>>(res).items;
        setSavedSearches(
          rows.filter(
            (s) => !s.ship_date || !dayjs(s.ship_date).isBefore(dayjs(), 'day')
          )
        );
      })
      .catch(() => {
        /* the dropdown is a convenience; the Saved tab surfaces errors */
      });
    return () => {
      cancelled = true;
    };
  }, [token, savedRefreshKey]);

  const resetResults = (): void => {
    reset();
    setSavedQuoteIds(new Set());
    setSavingQuoteIds(new Set());
    setPackagingOption(undefined);
    setCandidates(undefined);
    setReference(undefined);
    setBookingQuote(null);
  };

  // Token/company switch: drop in-flight results so the new identity cannot
  // see or act on the previous company's quotes.
  useEffect(() => {
    resetResults();
    setSavedSearches([]);
    setPrefill(null);
    // A fresh form: the previous company's entries and place suggestions go.
    setPrefillKey((k) => k + 1);
    setSaveSearchOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- identity boundary is token
  }, [token]);

  // The modes the company's vendors quote decide which tiles are enabled.
  useEffect(() => {
    let cancelled = false;
    // Until this company's config arrives (or if it fails) offer the
    // defaults, never the previous company's modes.
    setAvailable(DEFAULT_AVAILABLE);
    setModes(['air']);
    setNoVendors(false);
    setConfigFailed(false);
    setDoorToDoor(false);
    GetLaneConfig(token)
      .then((res) => {
        if (cancelled) return;
        const config = unwrapLanePayload<LaneConfig>(res);
        setNoVendors(config.modes.length === 0);
        setAvailable(new Set(config.modes));
        setDoorToDoor(!!config.door_to_door);
        setModes((current) => modesAfterConfig(current, config.modes));
      })
      .catch(() => {
        // keep the defaults, and say so: a search reports a missing vendor itself
        if (!cancelled) setConfigFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  /** A stored request has no boxes: offer the sites it carries. */
  const candidatesFor = (request: LaneSearchRequest): SearchCandidates => ({
    origin: placeValueFromRequest(request.origin, request.origin_sites),
    destination: placeValueFromRequest(
      request.destination,
      request.destination_sites
    ),
  });

  /** Puts a request into the form: its modes and its fields. */
  const showRequestInForm = (request: LaneSearchRequest): void => {
    setTab('search');
    setModes(request.modes);
    setPrefill(request);
    setPrefillKey((k) => k + 1);
  };

  const handleSearch = (
    request: LaneSearchRequest,
    meta?: SearchMeta
  ): void => {
    resetResults();
    setPackagingOption(meta?.packagingOption);
    setCandidates(meta?.candidates ?? candidatesFor(request));
    setReference(meta?.reference);
    // a re-run saved search may carry trucking the company can no longer quote
    const _ = start(doorToDoor ? request : withoutTrucking(request));
  };

  const handleSaveQuote = async (quote: NormalizedQuote): Promise<void> => {
    if (savingQuoteIds.has(quote.id)) return;
    setSavingQuoteIds((ids) => new Set(ids).add(quote.id));
    try {
      await SaveLaneQuote(token, quote.id, reference);
      setSavedQuoteIds((ids) => new Set(ids).add(quote.id));
      setSavedRefreshKey((k) => k + 1);
    } catch (err) {
      message.error(
        laneErrorMessage(err, 'Could not save the quote. Please try again.')
      );
    } finally {
      setSavingQuoteIds((ids) => {
        const next = new Set(ids);
        next.delete(quote.id);
        return next;
      });
    }
  };

  const handleShareQuote = async (quote: NormalizedQuote): Promise<void> => {
    try {
      await navigator.clipboard.writeText(
        quoteShareText(
          quote,
          search?.origin,
          search?.destination,
          search?.ship_date,
          search?.access_legs,
          truckingChoices
        )
      );
      message.success('Quote summary copied to the clipboard.');
    } catch {
      message.error('Could not copy the quote summary.');
    }
  };

  const handleSaveSearch = async (): Promise<void> => {
    if (!search || !saveSearchName.trim()) return;
    setSavingSearch(true);
    try {
      await SaveLaneSearch(token, search.id, saveSearchName.trim());
      setSaveSearchOpen(false);
      setSaveSearchName('');
      setSavedRefreshKey((k) => k + 1);
    } catch (err) {
      message.error(
        laneErrorMessage(err, 'Could not save the search. Please try again.')
      );
      setSaveSearchOpen(false);
    } finally {
      setSavingSearch(false);
    }
  };

  const handleRunSaved = (request: LaneSearchRequest): void => {
    if (bookingBusy) {
      message.warning(
        'A booking is in progress. Wait for it to finish before running another search.'
      );
      return;
    }
    const runnable = rerunnableRequest(request);
    showRequestInForm(runnable);
    handleSearch(runnable);
  };

  /** Shows a search that already ran, with its form filled in as it was. */
  const openSearch = async (searchId: string): Promise<void> => {
    if (bookingBusy) {
      message.warning(
        'A booking is in progress. Finish or leave it, then click "See more" again.'
      );
      return;
    }
    resetResults();
    setTab('search');
    const loaded = await load(searchId);
    if (!loaded) return;
    if (!loaded.params) {
      // Results without their inputs: don't leave the previous search's form.
      setPrefill(null);
      setPrefillKey((k) => k + 1);
      return;
    }
    const runnable = rerunnableRequest(loaded.params);
    showRequestInForm(runnable);
    setCandidates(candidatesFor(runnable));
  };

  // Runs after the token effect above, so its reset cannot wipe what opens here.
  useEffect(() => {
    if (!requestedSearchId) {
      openedSearchId.current = null;
      return;
    }
    if (!token || openedSearchId.current === requestedSearchId) return;
    openedSearchId.current = requestedSearchId;
    const _ = openSearch(requestedSearchId);
    // Drop only this param, so opening the same search again later is a new
    // request and any other query param (e.g. a help deep link) stays.
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete('search');
        return next;
      },
      { replace: true }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- opens once per requested id
  }, [requestedSearchId, token]);

  // ---------------------------------------------------------------------

  return (
    <div className={styles.pageShell}>
      <div className={styles.pageHeader}>
        <div className={styles.pageTitle}>Lane planner</div>
        <div className={styles.pageTabs}>
          <button
            type='button'
            className={`${styles.pageTab} ${tab === 'search' ? styles.pageTabActive : ''}`}
            onClick={() => setTab('search')}
          >
            Search
          </button>
          <button
            type='button'
            className={`${styles.pageTab} ${tab === 'saved' ? styles.pageTabActive : ''}`}
            onClick={() => setTab('saved')}
          >
            Saved
          </button>
        </div>
      </div>

      {bookingQuote && search && (
        <div
          className={tab === 'saved' ? styles.hidden : styles.bookingViewOpen}
        >
          <BookingPage
            key={bookingQuote.id}
            token={token}
            quote={bookingQuote}
            vendorName={vendorNameOf(search, bookingQuote.vendor)}
            origin={search.origin}
            destination={search.destination}
            onBackToResults={() => setBookingQuote(null)}
            onBusyChange={setBookingBusy}
            onViewShipments={() => navigate('/shipment')}
            onPlanAnother={() => {
              resetResults();
              setPrefill(null);
              setPrefillKey((k) => k + 1);
            }}
          />
        </div>
      )}

      {tab === 'saved' ? (
        <div className={`${styles.rightPane} ${styles.paneFlush}`}>
          <SavedPane
            token={token}
            refreshKey={savedRefreshKey}
            onRunSearch={handleRunSaved}
            onChanged={() => setSavedRefreshKey((k) => k + 1)}
            onQuoteRemoved={(quoteId) =>
              setSavedQuoteIds((ids) => {
                const next = new Set(ids);
                next.delete(quoteId);
                return next;
              })
            }
          />
        </div>
      ) : (
        <>
          <div
            className={`${styles.body} ${styles.bodyPadded} ${
              bookingQuote && search ? styles.hidden : ''
            }`}
          >
            <div className={styles.leftPane}>
              <div className={styles.saveSearchRow}>
                <Dropdown
                  disabled={savedSearches.length === 0}
                  menu={{
                    items: [
                      ...savedSearches
                        .slice(0, SAVED_SEARCH_DROPDOWN_LIMIT)
                        .map((saved) => ({
                          key: saved.id,
                          label: (
                            <div className={styles.savedMenuItem}>
                              {saved.ship_date && (
                                <span className={styles.savedMenuDate}>
                                  {dayjs(saved.ship_date).format('M/D/YY')}
                                </span>
                              )}
                              <span className={styles.savedMenuLane}>
                                {saved.saved_name ||
                                  `${saved.origin} → ${saved.destination}`}
                              </span>
                              <span className={styles.savedMenuModes}>
                                {saved.modes
                                  .map((mode) => MODE_TITLES[mode])
                                  .join(', ')}
                              </span>
                            </div>
                          ),
                        })),
                      ...(savedSearches.length > SAVED_SEARCH_DROPDOWN_LIMIT
                        ? [
                            { type: 'divider' as const },
                            {
                              key: '__view_all__',
                              label: `View all ${savedSearches.length} in Saved`,
                            },
                          ]
                        : []),
                    ],
                    onClick: ({ key }) => {
                      if (key === '__view_all__') {
                        setTab('saved');
                        return;
                      }
                      const saved = savedSearches.find((s) => s.id === key);
                      if (saved) handleRunSaved(saved.params);
                    },
                    style: { minWidth: 360, maxHeight: 320, overflowY: 'auto' },
                  }}
                  trigger={['click']}
                >
                  <button
                    type='button'
                    className={styles.seeSavedLink}
                    disabled={savedSearches.length === 0}
                  >
                    See saved searches
                    <svg width='10' height='10' viewBox='0 0 12 12' fill='none'>
                      <path
                        d='M2.5 4.5 6 8l3.5-3.5'
                        stroke='currentColor'
                        strokeWidth='1.6'
                        fill='none'
                      />
                    </svg>
                  </button>
                </Dropdown>
                <button
                  type='button'
                  className={styles.saveSearchLink}
                  disabled={!search || searching}
                  onClick={() => setSaveSearchOpen(true)}
                >
                  Save search
                </button>
              </div>

              <div
                className={`${styles.sectionTitle} ${styles.sectionTitleFirst}`}
              >
                Mode
              </div>
              <ModeTiles
                modes={modes}
                available={available}
                onChange={setModes}
              />

              <div className={styles.formSection}>
                {configFailed && (
                  <div className={styles.partialNote}>
                    We could not load which vendors your company has, so the
                    modes shown may not all be available. A search will say
                    which vendors could not run it.
                  </div>
                )}
                {noVendors ? (
                  <div className={styles.partialNote}>
                    No quoting vendor is enabled for your company yet. Ask an
                    admin to connect one in Settings &gt; Transport providers.
                  </div>
                ) : (
                  <>
                    <GoogleMapsScript />
                    <LaneSearchForm
                      key={prefillKey}
                      modes={modes}
                      searching={searching}
                      doorToDoor={doorToDoor}
                      prefill={prefill}
                      onSearch={handleSearch}
                      onClear={() => {
                        resetResults();
                        setPrefill(null);
                      }}
                    />
                  </>
                )}
              </div>
            </div>

            <div className={styles.rightPane}>
              <ResultsPane
                key={search?.id ?? 'none'}
                search={search}
                searching={searching}
                error={error}
                blockedReason={
                  noVendors
                    ? 'Rates need a connected vendor. An admin can connect one in Settings > Transport providers.'
                    : undefined
                }
                savedQuoteIds={savedQuoteIds}
                savingQuoteIds={savingQuoteIds}
                onSaveQuote={(quote) => handleSaveQuote(quote)}
                onShareQuote={handleShareQuote}
                truckingChoices={truckingChoices}
                onChooseTrucking={chooseTrucking}
                onRetryVendors={(ids) => retryVendors(ids)}
                candidates={candidates}
                onAddLanes={addLanes}
                onBookQuote={bookingOn ? setBookingQuote : undefined}
                packagingOption={packagingOption}
              />
            </div>
          </div>
        </>
      )}

      <Modal
        title='Save this search'
        open={saveSearchOpen}
        onCancel={() => setSaveSearchOpen(false)}
        onOk={() => handleSaveSearch()}
        okText={savingSearch ? 'Saving…' : 'Save'}
        okButtonProps={{
          disabled: !saveSearchName.trim() || savingSearch,
        }}
        destroyOnHidden
        width={420}
      >
        <CustomInput
          placeholder='e.g. PHL → AMS weekly pharma'
          value={saveSearchName}
          maxLength={120}
          onChange={(e) => setSaveSearchName(e.target.value)}
          onPressEnter={() => handleSaveSearch()}
        />
      </Modal>
    </div>
  );
};

export default LanePlannerPage;
